import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import * as colors from 'lib/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import AvatarIcon from 'ui-library/icons/AvatarIcon';

import {AvatarShape, AvatarSize} from './AvatarConstants';
import AvatarFrame, {AvatarFrameType} from './AvatarFrame';
import AvatarText from './AvatarText';

const AVATAR_DEFAULT_IMG = 'no_avatar.jpg';

export const NO_AVATAR_URL = `/static/img/${AVATAR_DEFAULT_IMG}`;

export interface HasAvatar {
  avatar_url?: string | null;
  avatarUrl?: string | null;
  email?: string;
  handle?: string;
  name?: string;
}

export interface AvatarProps {
  className?: string;
  size: AvatarSize;
  shape: AvatarShape;
  entity: HasAvatar;
  showTooltip: boolean;
  frame?: AvatarFrameType | null;
}

export function hasDefaultAvatar(hasAvatar: HasAvatar): boolean {
  return hasAvatar.avatar_url === NO_AVATAR_URL || hasAvatar.avatarUrl === NO_AVATAR_URL;
}

/**
 * Avatar initials are extracted from the handle, name, or email. This function picks which of those three
 * data to use.
 */
export function getEntityIdentifier(entity: HasAvatar): string {
  const identifier = entity.name || entity.handle || entity.email;
  assert(identifier != null, 'Avatar entity must have name, handle, or email specified.');
  return identifier;
}

export function getAvatarHeight(size: AvatarSize): number {
  // Keep in sync w/ less/components/avatars.less
  switch (size) {
    case 'xxs':
      return 16;
    case 'xs':
      return 20;
    case 'small':
      return 28;
    case 'newsmall':
      return 28;
    case 'large':
      return 64;
    default:
      assert(size === 'xl', 'Unexpected size');
      return 84;
  }
}

export function getAvatarInitials(entity: HasAvatar): string {
  // Use initials if the entity has two parts.
  if (entity.name) {
    const nameParts = entity.name.split(' ');
    if (nameParts.length === 2) {
      return nameParts.map((np) => np.charAt(0).toUpperCase()).join('');
    }
  }
  // Otherwise use the first letter of entity's name, handle, or email.
  const entityIdentifier = getEntityIdentifier(entity);
  return entityIdentifier.charAt(0).toUpperCase();
}

/**
 * An avatar based on an entity's avatar image or an identifier (handle, name, or email) if no image exists.
 */
export default function Avatar(props: AvatarProps): JSX.Element {
  const avatarUrl = props.entity.avatarUrl ?? props.entity.avatar_url;
  return (
    <div
      className={classNames(css(styles.avatarContainer))}
      {...getTestProps({
        component: 'Avatar',
        element: 'root',
      })}
    >
      <Tooltip
        isDisabled={!props.showTooltip}
        text={getEntityIdentifier(props.entity)}
        placement={FloatingPlacement.TOP}
      >
        <>
          {!avatarUrl || avatarUrl.endsWith(AVATAR_DEFAULT_IMG) ? (
            <AvatarText
              backgroundColor={colors.stringToColor(getEntityIdentifier(props.entity), 0.3)}
              className={props.className}
              size={props.size}
              shape={props.shape}
              text={getAvatarInitials(props.entity)}
            />
          ) : (
            <AvatarIcon
              alt={getEntityIdentifier(props.entity)}
              avatarUrl={avatarUrl}
              className={props.className}
              size={props.size}
              shape={props.shape}
            />
          )}
          {props.frame != null && <AvatarFrame type={props.frame} size={props.size} shape={props.shape} />}
        </>
      </Tooltip>
    </div>
  );
}
Avatar.defaultProps = {
  shape: 'circle',
  showTooltip: false,
};

const styles = StyleSheet.create({
  avatarContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    verticalAlign: 'middle',
  },
});
